<template>
  <teleport to="body">
  <div v-if="open" class="preview-shell" role="dialog" aria-modal="true" aria-label="預覽問卷">
    <header class="preview-bar">
      <strong>預覽</strong>
      <span>用目前畫面的內容。不會送出，也不會貼標。</span>
      <button type="button" @click="$emit('close')">關閉</button>
    </header>

    <main class="fill">
      <section v-if="done" class="stack">
        <article class="card header-card">
          <p class="message">{{ completionMessage || '謝謝您撥冗完成問卷填寫' }}</p>
          <p v-if="buttonNote" class="note">{{ buttonNote }}</p>
        </article>
        <article v-for="(item, index) in reviewAnswers" :key="'review-' + index" class="card">
          <p class="prompt">{{ item.prompt || '未命名的問題' }}</p>
          <p class="summary-value">{{ item.text }}</p>
        </article>
        <div v-if="completionButtons.length" class="done-actions">
          <button
            v-for="(button, index) in completionButtons"
            :key="'done-' + index"
            type="button"
            class="btn"
            :class="index === 0 ? 'primary' : 'ghost'"
            @click="previewAction(button)"
          >
            {{ button.label || '按鈕' }}
          </button>
        </div>
        <div class="nav">
          <button type="button" class="btn text" @click="done = false">返回問卷</button>
        </div>
      </section>

      <form v-else class="stack" @submit.prevent="next">
        <p v-if="pages.length > 1" class="page-count">第 {{ pageIndex + 1 }} 區段，共 {{ pages.length }} 區段</p>
        <div v-if="pages.length > 1" class="progress" role="progressbar" :aria-valuenow="pageIndex + 1" :aria-valuemax="pages.length">
          <span :style="{ width: progress + '%' }"></span>
        </div>

        <article v-if="pageIndex === 0" class="card header-card">
          <h1>{{ name || '表單名稱' }}</h1>
          <p v-if="description" class="desc">{{ description }}</p>
          <p v-if="hasRequired" class="required-note">* 表示必填問題</p>
          <div v-for="field in unplacedFields" :key="field.fieldKey" class="intake">
            <span class="intake-label">{{ field.label || field.fieldKey }}</span>
            <span class="intake-value">預覽時沒有帶入值</span>
          </div>
        </article>
        <article v-else-if="page.title || page.description" class="card section-card">
          <h2 v-if="page.title">{{ page.title }}</h2>
          <p v-if="page.description" class="desc">{{ page.description }}</p>
        </article>

        <article
          v-for="block in page.blocks || []"
          v-show="showBlock(block)"
          :key="block.id"
          class="card"
          :class="{ invalid: block.type === 'question' && questionError(block.question), 'image-card': block.type === 'image' }"
        >
          <template v-if="block.type === 'text'">
            <h2 v-if="block.title" class="text-title">{{ block.title }}</h2>
            <p v-if="block.text && block.showDescription !== false" class="desc">{{ block.text }}</p>
          </template>
          <img v-if="block.type === 'image' && block.imageUrl" :src="block.imageUrl" alt="" />
          <p v-else-if="block.type === 'image'" class="desc">尚未上傳圖片</p>
          <div v-if="block.type === 'intake' && intakeField(block)" class="intake">
            <span class="intake-label">{{ intakeField(block).label }}</span>
            <span class="intake-value">預覽時沒有帶入值</span>
          </div>
          <template v-if="block.type === 'question' && block.question && answers[block.question.id]">
            <p class="prompt">
              {{ block.question.prompt || '未命名的問題' }}
              <span v-if="block.question.required" class="star">*</span>
            </p>
            <p v-if="block.question.helpText && block.question.showDescription" class="help">{{ block.question.helpText }}</p>
            <p v-if="maxOf(block.question)" class="help">最多勾選 {{ maxOf(block.question) }} 項</p>
            <input
              v-if="block.question.questionType === 'short_text'"
              v-model="answers[block.question.id].textValue"
              class="line-input"
              type="text"
              :placeholder="block.question.placeholder || '你的回答'"
              @input="clearQuestionError(block.question.id)"
            />
            <textarea
              v-else-if="block.question.questionType === 'long_text'"
              v-model="answers[block.question.id].textValue"
              class="box-input"
              rows="4"
              :placeholder="block.question.placeholder || '你的回答'"
              @input="clearQuestionError(block.question.id)"
            ></textarea>
            <div v-else-if="block.question.questionType === 'scale'" class="scale" role="radiogroup">
              <label v-for="option in shownOptions(block.question)" :key="option.id" class="scale-item">
                <input
                  type="radio"
                  :name="block.question.id"
                  :checked="selected(block.question, option.id)"
                  @change="toggleOption(block.question, option.id)"
                />
                <span>{{ option.label || '選項' }}</span>
              </label>
            </div>
            <div v-else class="choices">
              <label
                v-for="option in shownOptions(block.question)"
                :key="option.id"
                class="choice"
                :class="{ locked: optionLocked(block.question, option.id) }"
              >
                <input
                  :type="block.question.questionType === 'multi' ? 'checkbox' : 'radio'"
                  :name="block.question.id"
                  :checked="selected(block.question, option.id)"
                  :disabled="optionLocked(block.question, option.id)"
                  @change="toggleOption(block.question, option.id)"
                />
                <span class="choice-label">{{ option.label || '選項' }}</span>
                <input
                  v-if="option.isOther && option.otherTextEnabled"
                  v-model="answers[block.question.id].otherText"
                  class="other-input"
                  type="text"
                  placeholder="請補充"
                  @click.stop
                  @input="clearQuestionError(block.question.id)"
                  @focus="selectOther(block.question, option.id)"
                />
              </label>
            </div>
            <p v-if="questionError(block.question)" class="field-error">{{ questionError(block.question) }}</p>
          </template>
        </article>

        <div class="nav">
          <button type="submit" class="btn primary">{{ atEnd ? '送出' : '下一頁' }}</button>
          <button v-if="history.length > 1" type="button" class="btn text" @click="back">上一頁</button>
        </div>
      </form>
    </main>
  </div>
  </teleport>
</template>

<script>
export default {
  name: 'QuestionnairePreview',
  props: {
    open: { type: Boolean, default: false },
    name: { type: String, default: '' },
    description: { type: String, default: '' },
    pages: { type: Array, default: () => [] },
    intakeFields: { type: Array, default: () => [] },
    completionMessage: { type: String, default: '' },
    completionButtons: { type: Array, default: () => [] }
  },
  emits: ['close'],
  data() {
    return {
      pageIndex: 0,
      history: [0],
      answers: {},
      optionOrder: {},
      invalidIds: [],
      done: false,
      buttonNote: ''
    };
  },
  computed: {
    page() {
      return this.pages[this.pageIndex] || { blocks: [] };
    },
    progress() {
      if (!this.pages.length) return 0;
      return ((this.pageIndex + 1) / this.pages.length) * 100;
    },
    atEnd() {
      return this.resolveNext() === 'submit';
    },
    hasRequired() {
      return this.pages.some((page) => (page.blocks || []).some((block) => block.type === 'question' && block.question?.required));
    },
    unplacedFields() {
      const placed = new Set();
      this.pages.forEach((page) => {
        (page.blocks || []).forEach((block) => {
          if (block.type === 'intake' && block.fieldKey) placed.add(block.fieldKey);
        });
      });
      return (this.intakeFields || []).filter((field) => field.visible && field.fieldKey && !placed.has(field.fieldKey));
    },
    reviewAnswers() {
      const visited = new Set(this.history);
      const items = [];
      this.pages.forEach((page, index) => {
        if (!visited.has(index)) return;
        (page.blocks || []).forEach((block) => {
          if (block.type !== 'question') return;
          items.push({
            prompt: block.question.prompt,
            text: this.answerText(block.question)
          });
        });
      });
      return items;
    }
  },
  watch: {
    open(value) {
      document.removeEventListener('keydown', this.onKeydown);
      if (!value) return;
      this.reset();
      document.addEventListener('keydown', this.onKeydown);
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') this.$emit('close');
    },
    reset() {
      this.pageIndex = 0;
      this.history = [0];
      this.invalidIds = [];
      this.done = false;
      this.buttonNote = '';
      const answers = {};
      const optionOrder = {};
      this.pages.forEach((page) => {
        (page.blocks || []).forEach((block) => {
          if (block.type !== 'question' || !block.question?.id) return;
          answers[block.question.id] = { optionIds: [], otherText: '', textValue: '' };
          optionOrder[block.question.id] = this.shuffleOptions(block.question);
        });
      });
      this.answers = answers;
      this.optionOrder = optionOrder;
    },
    shownOptions(question) {
      return this.optionOrder[question.id] || question.options || [];
    },
    selected(question, optionId) {
      return (this.answers[question.id]?.optionIds || []).includes(optionId);
    },
    maxOf(question) {
      const max = Number(question.maxSelections);
      return question.questionType === 'multi' && Number.isInteger(max) && max >= 1 ? max : null;
    },
    optionLocked(question, optionId) {
      const max = this.maxOf(question);
      if (!max) return false;
      const picked = this.answers[question.id]?.optionIds || [];
      return picked.length >= max && !picked.includes(optionId);
    },
    shuffleOptions(question) {
      const options = [...(question.options || [])];
      const others = options.filter((option) => option.isOther);
      const rest = options.filter((option) => !option.isOther);
      const canShuffle = question.shuffleOptions && (question.questionType === 'single' || question.questionType === 'multi');
      if (canShuffle) {
        for (let index = rest.length - 1; index > 0; index -= 1) {
          const swap = Math.floor(Math.random() * (index + 1));
          [rest[index], rest[swap]] = [rest[swap], rest[index]];
        }
      }
      return [...rest, ...others];
    },
    resolveNext() {
      const page = this.pages[this.pageIndex];
      let destination = '';
      for (const block of page?.blocks || []) {
        const question = block.type === 'question' ? block.question : null;
        if (!question?.branchByAnswer || question.questionType !== 'single') continue;
        const selectedId = this.answers[question.id]?.optionIds?.[0];
        const option = (question.options || []).find((item) => item.id === selectedId);
        if (!option) continue;
        destination = option.goToPageId || '';
      }
      if (destination === 'submit') return 'submit';
      if (destination) {
        const target = this.pages.findIndex((item) => item.id === destination);
        if (target > this.pageIndex) return target;
      }
      return this.pageIndex < this.pages.length - 1 ? this.pageIndex + 1 : 'submit';
    },
    back() {
      if (this.history.length < 2) return;
      this.history.pop();
      this.pageIndex = this.history[this.history.length - 1];
      this.invalidIds = [];
    },
    toggleOption(question, optionId) {
      const answer = this.answers[question.id];
      if (!answer) return;
      this.clearQuestionError(question.id);
      if (question.questionType === 'multi') {
        if (answer.optionIds.includes(optionId)) {
          answer.optionIds = answer.optionIds.filter((id) => id !== optionId);
          return;
        }
        const max = this.maxOf(question);
        if (max && answer.optionIds.length >= max) return;
        answer.optionIds = [...answer.optionIds, optionId];
        return;
      }
      answer.optionIds = [optionId];
    },
    selectOther(question, optionId) {
      const answer = this.answers[question.id];
      if (!answer) return;
      if (question.questionType === 'multi') {
        if (answer.optionIds.includes(optionId)) return;
        const max = this.maxOf(question);
        if (max && answer.optionIds.length >= max) return;
        answer.optionIds = [...answer.optionIds, optionId];
        return;
      }
      answer.optionIds = [optionId];
    },
    showBlock(block) {
      if (block.type !== 'intake') return true;
      return Boolean(this.intakeField(block));
    },
    intakeField(block) {
      const field = (this.intakeFields || []).find((item) => item.fieldKey === block.fieldKey);
      if (!field || !field.visible) return null;
      return { label: field.label || field.fieldKey };
    },
    answerText(question) {
      const answer = this.answers[question.id];
      if (!answer) return '—';
      if (question.questionType === 'short_text' || question.questionType === 'long_text') {
        return String(answer.textValue || '').trim() || '—';
      }
      const labels = (question.options || [])
        .filter((option) => answer.optionIds.includes(option.id))
        .map((option) => option.label)
        .filter((label) => String(label || '').trim());
      const parts = [];
      if (labels.length) parts.push(labels.join('、'));
      if (String(answer.otherText || '').trim()) parts.push(String(answer.otherText).trim());
      return parts.join('、') || '—';
    },
    answerProblem(question) {
      const answer = this.answers[question.id];
      if (!answer) return '';
      const max = this.maxOf(question);
      if (max && answer.optionIds.length > max) return `最多勾選 ${max} 項`;
      const other = (question.options || []).find((option) =>
        answer.optionIds.includes(option.id) && option.isOther && option.otherTextEnabled && option.otherTextRequired
      );
      if (other && !String(answer.otherText || '').trim()) return '請補充說明';
      if (!question.required) return '';
      if (question.questionType === 'short_text' || question.questionType === 'long_text') {
        if (!String(answer.textValue || '').trim()) return '這是必填問題';
      } else if (answer.optionIds.length === 0) {
        return '這是必填問題';
      }
      return '';
    },
    questionError(question) {
      if (!question || !this.invalidIds.includes(question.id)) return '';
      return this.answerProblem(question);
    },
    clearQuestionError(questionId) {
      this.invalidIds = this.invalidIds.filter((id) => id !== questionId);
    },
    next() {
      const ids = [];
      for (const block of this.page.blocks || []) {
        if (block.type !== 'question') continue;
        if (this.answerProblem(block.question)) ids.push(block.question.id);
      }
      this.invalidIds = ids;
      if (ids.length) return;
      const nextIndex = this.resolveNext();
      if (nextIndex === 'submit') {
        this.done = true;
        this.buttonNote = '';
        return;
      }
      this.history.push(nextIndex);
      this.pageIndex = nextIndex;
    },
    previewAction(button) {
      if (button.actionType === 'url') this.buttonNote = button.uri ? `預覽不會開啟網址：${button.uri}` : '這個按鈕還沒有網址';
      else if (button.actionType === 'tel') this.buttonNote = button.phone ? `預覽不會撥打：${button.phone}` : '這個按鈕還沒有電話';
      else this.buttonNote = '預覽不會關閉頁面';
    }
  }
};
</script>

<style scoped>
.preview-shell {
  position: fixed;
  inset: 0;
  z-index: 12000;
  display: flex;
  flex-direction: column;
  background: #f0ebf8;
}
.preview-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e6e8f0;
  color: #202124;
}
.preview-bar span { flex: 1; color: #5f6368; font-size: 13px; }
.preview-bar button {
  border: 0;
  background: transparent;
  color: #5746e3;
  font: inherit;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
}
.preview-bar button:hover { background: #f4f2ff; }
.fill { flex: 1; overflow: auto; color: #202124; font-size: 14px; line-height: 1.5; }
.stack {
  width: min(640px, 100%);
  margin: 0 auto;
  padding: 12px 12px 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.page-count { margin: 0; color: #5f6368; font-size: 13px; }
.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
  padding: 22px 24px;
}
.header-card { border-top: 10px solid #5746e3; padding-top: 18px; }
.card.invalid { outline: 1px solid #d93025; }
.progress { height: 6px; background: #fff; border-radius: 6px; overflow: hidden; }
.progress span { display: block; height: 100%; background: #5746e3; }
h1, h2, .text-title, .prompt, .desc, .help, .message, .note, .field-error, .required-note, .summary-value { margin: 0; white-space: pre-wrap; }
h1 { font-size: 32px; font-weight: 400; line-height: 1.3; }
h2, .text-title { font-size: 24px; font-weight: 400; line-height: 1.35; }
.desc { margin-top: 8px; }
.required-note { margin-top: 12px; color: #d93025; }
.prompt { font-size: 16px; }
.star { color: #d93025; margin-left: 2px; }
.help, .note, .page-count { color: #5f6368; }
.help { margin-top: 4px; font-size: 13px; }
.field-error { color: #d93025; font-size: 12px; margin-top: 8px; }
.card.invalid .prompt { color: #d93025; }
.choices { margin-top: 8px; }
.choice { display: flex; align-items: center; gap: 12px; min-height: 40px; cursor: pointer; }
.choice.locked { opacity: 0.38; cursor: default; }
.choice input, .scale-item input { width: 20px; height: 20px; margin: 0; accent-color: #5746e3; flex: 0 0 auto; }
.other-input, .line-input, .box-input { font: inherit; color: #202124; background: transparent; }
.other-input, .line-input { border: 0; border-bottom: 1px solid #dadce0; padding: 8px 0; }
.other-input { flex: 1; min-width: 96px; }
.line-input { width: 100%; margin-top: 8px; }
.box-input { width: 100%; margin-top: 12px; border: 1px solid #dadce0; border-radius: 4px; padding: 12px; resize: vertical; min-height: 96px; }
.scale { display: flex; gap: 4px; margin-top: 16px; overflow-x: auto; }
.scale-item { flex: 1 0 56px; display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; font-size: 12px; cursor: pointer; }
.intake { display: flex; flex-direction: column; gap: 2px; margin-top: 12px; }
.intake-label { color: #5f6368; font-size: 12px; }
.intake-value, .summary-value, .message { font-size: 16px; }
.summary-value { margin-top: 8px; }
.note { margin-top: 12px; }
.image-card { padding: 12px; }
img { display: block; width: 100%; border-radius: 4px; }
.done-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.nav { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.btn { border: 0; border-radius: 4px; min-height: 36px; padding: 8px 24px; font: inherit; font-weight: 500; cursor: pointer; }
.btn.primary { background: #5746e3; color: #fff; }
.btn.text { background: transparent; color: #5746e3; }
.btn.ghost { background: #fff; color: #5746e3; box-shadow: inset 0 0 0 1px #dadce0; }
@media (max-width: 480px) {
  .card { padding: 16px; }
  h1 { font-size: 28px; }
  .preview-bar span { display: none; }
}
</style>
